import { IFaxTag } from '@dataTypes/fax.type'
import { MOCK_FAX_TAGS } from '@constants/Message/faxMock'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { enableMockFaxData } from './fax.api'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_FAX')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type GenericResponseData = Record<string, unknown>

type FaxTagResponseData = { fax_tag?: IFaxTag }
type FaxTagListResponseData = { fax_tag?: IFaxTag[] }

const mockFaxTagsResponse = (): ApiResponse<FaxTagListResponseData> => ({
  statusCode: 200,
  message: 'OK (mock)',
  data: { fax_tag: MOCK_FAX_TAGS },
})

const getHeaders = (dbName?: string): Record<string, string> =>
  dbName ? { ...headers, 'x-db-name': dbName } : headers

export const getAllFaxTag = async (
  dbName?: string,
): Promise<ApiResponse<FaxTagListResponseData>> => {
  if (enableMockFaxData()) {
    return mockFaxTagsResponse()
  }

  try {
    const response = await axios.get<ApiResponse<FaxTagListResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/tag/all`,
      { headers: getHeaders(dbName) },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllFaxTag:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getFaxTagById = async (
  id: string,
): Promise<ApiResponse<FaxTagResponseData>> => {
  try {
    const response = await axios.get<ApiResponse<FaxTagResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/tag/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getFaxTagById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createFaxTag = async (
  data: IFaxTag,
): Promise<ApiResponse<FaxTagResponseData>> => {
  try {
    const response = await axios.post<ApiResponse<FaxTagResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/tag`,
      data,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createFaxTag:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateFaxTagById = async (
  fax_tag_id: string,
  data: Partial<IFaxTag>,
): Promise<ApiResponse<FaxTagResponseData>> => {
  try {
    const response = await axios.put<ApiResponse<FaxTagResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/tag`,
      { fax_tag_id, data },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateFaxTagById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteFaxTagById = async (
  id: string,
): Promise<ApiResponse<GenericResponseData>> => {
  try {
    const response = await axios.delete<ApiResponse<GenericResponseData>>(
      `${BUSINESS_LAYER_API}/api/v1/fax/tag/${id}`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteFaxTagById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
